import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  parseMeetResults,
  parseMeetResultsFromUrl,
  parseMeetResultsFromPdf
} from '../../utils/meetResultsParser';
import { useNavigate } from 'react-router-dom';

const MeetManager = () => {
  const [meets, setMeets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sourceType, setSourceType] = useState('file');
  const [file, setFile] = useState(null);
  const [meetUrl, setMeetUrl] = useState('');
  const [meetName, setMeetName] = useState('');
  const [meetDate, setMeetDate] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadMeets();
  }, []);

  const loadMeets = async () => {
    setLoading(true);
    try {
      const meetsRef = collection(db, 'meets');
      const q = query(meetsRef, orderBy('date', 'desc'));
      const snapshot = await getDocs(q);
      const meetsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMeets(meetsData);
    } catch (err) {
      console.error('Error loading meets:', err);
      setError('Failed to load meets');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf'
      ];

      if (
        validTypes.includes(selectedFile.type) ||
        selectedFile.name.endsWith('.xlsx') ||
        selectedFile.name.endsWith('.xls') ||
        selectedFile.name.endsWith('.pdf')
      ) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please upload a valid file (.xlsx, .xls, or .pdf)');
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    const missingFile = sourceType === 'file' && !file;
    const missingUrl = sourceType === 'url' && !meetUrl.trim();
    if (!meetName || !meetDate || missingFile || missingUrl) {
      setError(
        sourceType === 'url'
          ? 'Please provide meet name, date, and a meet URL'
          : 'Please provide meet name, date, and file'
      );
      return;
    }

    setUploading(true);
    setError(null);

    try {
      let parsedResults;
      if (sourceType === 'url') {
        parsedResults = await parseMeetResultsFromUrl(meetUrl);
      } else if (file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf') {
        parsedResults = await parseMeetResultsFromPdf(file);
      } else {
        parsedResults = await parseMeetResults(file);
      }

      // Save to Firebase
      const meetsRef = collection(db, 'meets');
      await addDoc(meetsRef, {
        name: meetName,
        date: meetDate,
        results: parsedResults,
        uploadedAt: new Date().toISOString(),
        fileName: sourceType === 'file' ? file.name : null,
        sourceType,
        sourceUrl: sourceType === 'url' ? meetUrl.trim() : null
      });

      // Reset form
      setFile(null);
      setMeetUrl('');
      setMeetName('');
      setMeetDate('');

      // Reload meets
      await loadMeets();

      alert('Meet uploaded successfully!');
    } catch (err) {
      console.error('Error uploading meet:', err);
      setError(
        `Failed to upload meet. ${err.message || 'Please ensure the file has the correct format.'} ` +
        'If URL scrape fails in-browser, run: npm run scrape:meet -- <meet-url> <output.xlsx> and upload that file.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (meetId, meetName) => {
    if (!window.confirm(`Are you sure you want to delete "${meetName}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'meets', meetId));
      await loadMeets();
      alert('Meet deleted successfully');
    } catch (err) {
      console.error('Error deleting meet:', err);
      alert('Failed to delete meet');
    }
  };

  const viewSeasonAverages = () => {
    navigate('/season-averages');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Meet Manager
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Upload and manage gymnastics meet results
              </p>
            </div>
            <button
              onClick={viewSeasonAverages}
              disabled={meets.length === 0}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-all ${
                meets.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              View Season Averages
            </button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Upload New Meet
          </h2>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Data Source *
            </label>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setSourceType('file');
                  setError(null);
                }}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  sourceType === 'file'
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                Upload Excel
              </button>
              <button
                type="button"
                onClick={() => {
                  setSourceType('url');
                  setError(null);
                }}
                className={`px-4 py-2 rounded-lg border transition-all ${
                  sourceType === 'url'
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                Scrape Meet URL
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Meet Name *
              </label>
              <input
                type="text"
                value={meetName}
                onChange={(e) => setMeetName(e.target.value)}
                placeholder="e.g., USA Invitational"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Meet Date *
              </label>
              <input
                type="date"
                value={meetDate}
                onChange={(e) => setMeetDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {sourceType === 'file' ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Results File *
              </label>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {file ? file.name : 'Click to upload Excel or PDF file'}
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.pdf,application/pdf"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                PDF support is for MeetScoresOnline-style official results PDFs.
              </p>
            </div>
          ) : (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Meet Scores URL *
              </label>
              <input
                type="url"
                value={meetUrl}
                onChange={(e) => setMeetUrl(e.target.value)}
                placeholder="https://meetscoresonline.com/..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                If direct scraping is blocked by CORS, the importer automatically tries a read-only proxy.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={
              uploading ||
              !meetName ||
              !meetDate ||
              (sourceType === 'file' && !file) ||
              (sourceType === 'url' && !meetUrl.trim())
            }
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
              uploading ||
              !meetName ||
              !meetDate ||
              (sourceType === 'file' && !file) ||
              (sourceType === 'url' && !meetUrl.trim())
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:transform active:scale-95'
            }`}
          >
            {uploading ? 'Uploading...' : sourceType === 'url' ? 'Scrape and Upload Meet' : 'Upload Meet'}
          </button>
        </div>

        {/* Meets List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Uploaded Meets ({meets.length})
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading meets...</p>
            </div>
          ) : meets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400">No meets uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {meets.map((meet) => (
                <div
                  key={meet.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {meet.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Date: {new Date(meet.date).toLocaleDateString()} |
                        {' '}{meet.results.totalGymnasts} gymnasts |
                        {' '}{Object.keys(meet.results.groups).length} categories
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Uploaded: {new Date(meet.uploadedAt).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(meet.id, meet.name)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetManager;
